import React from 'react';
import './reportCard.css';
import ReportHeader from './ReportHeader';
import StudentBio from './StudentBio';
import SubjectsTable from './SubjectsTable';
import SecondarySubjectsTable from './SecondarySubjectsTable';
import ProjectsTable from './ProjectsTable';
import ProgressTable from './ProgressTable';
import ConductSection from './ConductSection';
import CommentsSection from './CommentsSection';
import GradeKeyTable from './GradeKeyTable';
import ReportFooter from './ReportFooter';

/**
 * ReportDocument
 * Props:
 *   report        - full report card object (with student, class, stream, term, subjects_data)
 *   school        - school object (name, logo, motto, address, phone, email)
 *   conduct       - array of StudentConduct entries for this student/term
 *   generalComment - GeneralComment object (class_teacher_comment, headteacher_comment)
 *   nextTermDate  - string, optional
 *   template      - report card template settings (visibility, paper size, orientation)
 */
const ReportDocument = React.forwardRef(function ReportDocument(
  { report, school, conduct = [], generalComment = null, nextTermDate: nextTermDateProp = null, template = null, gradeBoundaries = [] },
  ref
) {
  const displayPreferences = report?.display_preferences || {};
  const showAggregate = template?.show_aggregate ?? true;
  const showTotalAggregate = template?.show_total_aggregate ?? true;
  const showStream = (template?.show_stream ?? true)
    && (displayPreferences.show_stream ?? true);
  const showClassPosition = (template?.show_class_position ?? true)
    && (displayPreferences.show_class_position ?? true);
  const showStreamPosition = (template?.show_stream_position ?? true)
    && (displayPreferences.show_stream_position ?? true);
  const showSubjectRank = template != null
    ? (template?.show_subject_rank ?? false)
    : (displayPreferences.show_subject_rank ?? false);
  const showDivision = (template?.show_division ?? true)
    && (displayPreferences.show_division ?? true);
  const showConduct = template?.show_conduct ?? true;
  const showSubjectComments = template?.show_subject_comments ?? true;
  const showMarks = template?.show_marks ?? true;
  const showGrade = template?.show_grade ?? true;
  const assessmentDisplayMode = template?.assessment_display_mode || 'weighted';
  const paperSize = (template?.paper_size || 'A4').toLowerCase();
  const orientation = (template?.orientation || 'Portrait').toLowerCase();

  const nextTermDate = report?.next_term_begins
    ? new Date(report.next_term_begins).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : nextTermDateProp ?? null;

  const isTemplateOne = template?.assessment_display_mode === 'progress';
  const isSecondaryTemplate = template?.assessment_display_mode === 'secondary';

  return (
    <div className={`rc-page rc-${paperSize} rc-${orientation}`} ref={ref}>
      <ReportHeader school={school} report={report} student={report?.student} />

      {isTemplateOne ? (
        <>
          <StudentBio report={report} showAttendance showPromotion showTotalAggregate={showTotalAggregate} showDivision={showDivision} showStream={showStream} />

          {/* Table 2: Academic Progress + Table 3: Summary */}
          <ProgressTable
            report={report}
            showClassPosition={showClassPosition}
            showStreamPosition={showStreamPosition}
            showDivision={showDivision}
            showAggregate={showAggregate}
            showTotalAggregate={showTotalAggregate}
            showMarks={showMarks}
            showGrade={showGrade}
            showSubjectComments={showSubjectComments}
          />

          {/* Conduct */}
          {showConduct && <ConductSection conduct={conduct} generalComment={generalComment} />}

          {/* Comments */}
          <CommentsSection generalComment={generalComment} />
        </>
      ) : isSecondaryTemplate ? (
        <>
          <StudentBio report={report} showAttendance showPromotion
            showClassPosition={showClassPosition}
            showStreamPosition={showStreamPosition}
            showDivision={showDivision}
            showTotalAggregate={showTotalAggregate}
            showStream={showStream}
          />
          <SecondarySubjectsTable
            report={report}
            showAggregate={showAggregate}
            showSubjectComments={showSubjectComments}
            showSubjectRank={showSubjectRank}
            showMarks={showMarks}
            showGrade={showGrade}
          />
          <ProjectsTable report={report} activities={report?.cocurricular_activities} showComments={showSubjectComments} />
          {showConduct && <ConductSection conduct={conduct} generalComment={generalComment} />}
          <CommentsSection generalComment={generalComment} />
        </>
      ) : (
        <>
          <StudentBio report={report} showAttendance showPromotion
            showClassPosition={showClassPosition}
            showStreamPosition={showStreamPosition}
            showDivision={showDivision}
            showTotalAggregate={showTotalAggregate}
            showStream={showStream}
          />
          <SubjectsTable
            report={report}
            showAggregate={showAggregate}
            showTotalAggregate={showTotalAggregate}
            showSubjectComments={showSubjectComments}
            showSubjectRank={showSubjectRank}
            showMarks={showMarks}
            showGrade={showGrade}
            assessmentDisplayMode={assessmentDisplayMode}
          />
          {showConduct && <ConductSection conduct={conduct} generalComment={generalComment} />}
          <CommentsSection generalComment={generalComment} />
        </>
      )}

      <div style={{ marginTop: 'auto' }}>
        <GradeKeyTable boundaries={gradeBoundaries} />
        <ReportFooter report={report} nextTermDate={nextTermDate} />
      </div>
    </div>
  );
});

export default ReportDocument;
